import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es";
import "flatpickr/dist/flatpickr.min.css";
import ReactQuill from "react-quill-new";
import Select2 from "../UI/Select2";
import { getMisEstudiantesService } from "../../services/classroom/classroom.service";

const initialFormState = {
  titulo: "",
  descripcion: "",
  fecha_entrega: "",
  alumno: "",
};

const ActividadFormModal = ({
  show,
  onHide,
  onSubmit,
  submitting = false,
  eventData = null,
  title = "Nueva Actividad",
}) => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const datepickerRef = useRef(null);
  const timepickerRef = useRef(null);
  const dateInstanceRef = useRef(null);
  const timeInstanceRef = useRef(null);

  const isEditing = !!eventData;

  // Helpers: extraer fecha y hora de un ISO "YYYY-MM-DDTHH:MM:SS"
  const splitDateTime = (isoString) => {
    if (!isoString || !isoString.includes("T")) return { date: "", time: "23:59" };
    const [date, time] = isoString.split("T");
    return { date, time: time?.substring(0, 5) || "23:59" };
  };

  // Combinar fecha y hora en ISO local
  const combineDateTime = (dateStr, timeStr) => {
    if (!dateStr) return "";
    const time = timeStr || "23:59";
    return `${dateStr}T${time}:00`;
  };

  // Inicializar formulario
  useEffect(() => {
    if (show) {
      if (eventData) {
        setForm({
          titulo: eventData.titulo || "",
          descripcion: eventData.descripcion || "",
          fecha_entrega: eventData.fecha_entrega || "",
          alumno: eventData.alumno || "",
        });
      } else {
        setForm(initialFormState);
      }
      setErrors({});
    }
  }, [show, eventData]);

  // fetchOptions para Select2: llama a la API y mapea a { id, label }
  const fetchAlumnos = async (searchTerm) => {
    const response = await getMisEstudiantesService(searchTerm);
    const data = Array.isArray(response) ? response : response?.results ?? [];
    return data.map((est) => ({
      id: est.estudiante_id ?? est.id,
      label: est.estudiante_nombre ?? est.nombre_completo ?? est.nombre ?? est.email ?? `Estudiante #${est.estudiante_id ?? est.id}`,
    }));
  };

  // Configuración del editor Quill (mini)
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "link",
  ];

  // Inicializar flatpickr para FECHA
  useEffect(() => {
    if (show && datepickerRef.current) {
      const { date: currentDate } = splitDateTime(form.fecha_entrega);

      dateInstanceRef.current = flatpickr(datepickerRef.current, {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        locale: Spanish,
        minDate: "today",
        defaultDate: currentDate || undefined,
        onChange: ([date]) => {
          if (date) {
            const dateStr = date.toISOString().slice(0, 10);
            const { time } = splitDateTime(form.fecha_entrega);
            const combined = combineDateTime(dateStr, time);
            setForm((prev) => ({ ...prev, fecha_entrega: combined }));
            if (errors.fecha_entrega) {
              setErrors((prev) => ({ ...prev, fecha_entrega: null }));
            }
          } else {
            setForm((prev) => ({ ...prev, fecha_entrega: "" }));
          }
        },
      });
    }

    return () => {
      dateInstanceRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Inicializar flatpickr para HORA
  useEffect(() => {
    if (show && timepickerRef.current) {
      const { time: currentTime } = splitDateTime(form.fecha_entrega);

      timeInstanceRef.current = flatpickr(timepickerRef.current, {
        noCalendar: true,
        enableTime: true,
        dateFormat: "H:i",
        time_24hr: true,
        minuteIncrement: 5,
        locale: Spanish,
        defaultDate: currentTime || "23:59",
        onChange: ([date]) => {
          if (date) {
            const pad = (n) => String(n).padStart(2, "0");
            const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
            const { date: datePart } = splitDateTime(form.fecha_entrega);
            const combined = combineDateTime(datePart || new Date().toISOString().slice(0, 10), timeStr);
            setForm((prev) => ({ ...prev, fecha_entrega: combined }));
          }
        },
      });
    }

    return () => {
      timeInstanceRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Actualizar flatpickrs cuando cambia eventData
  useEffect(() => {
    if (show && eventData?.fecha_entrega) {
      const { date, time } = splitDateTime(eventData.fecha_entrega);
      if (date) dateInstanceRef.current?.setDate(date, false);
      if (time) timeInstanceRef.current?.setDate(time, false);
    }
  }, [show, eventData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.titulo.trim()) errs.titulo = "El título es requerido";
    if (form.titulo.length > 255) errs.titulo = "Máximo 255 caracteres";
    if (!form.fecha_entrega) errs.fecha_entrega = "La fecha de entrega es requerida";

    // Validar que la fecha no sea pasada (solo al crear)
    if (!isEditing && form.fecha_entrega) {
      const fecha = new Date(form.fecha_entrega);
      if (fecha < new Date()) {
        errs.fecha_entrega = "La fecha no puede ser en el pasado";
      }
    }

    // Alumno requerido (solo al crear)
    if (!isEditing && !form.alumno) errs.alumno = "Debes seleccionar un alumno";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || undefined,
      fecha_entrega: form.fecha_entrega,
    };

    // Solo incluir alumno al crear
    if (!isEditing) {
      payload.alumno = Number(form.alumno);
    }

    onSubmit(payload);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onHide();
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">
          {/* Header */}
          <div className="modal-header py-20 px-24 border-0 classroom-modal-header">
            <div className="d-flex align-items-center gap-12">
              <div className="w-40-px h-40-px bg-primary-50 d-flex align-items-center justify-content-center radius-10">
                <Icon
                  icon={isEditing ? "mdi:pencil-outline" : "mdi:plus"}
                  className="text-primary-600"
                  style={{ fontSize: "20px" }}
                />
              </div>
              <h5 className="modal-title fw-semibold mb-0">{title}</h5>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
              disabled={submitting}
            />
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="modal-body px-24 py-20">
                 {/* Alumno + Fecha de entrega (misma línea) */}
              <div className="row g-16 mb-20">
                {/* Alumno (solo al crear) */}
                {!isEditing && (
                  <div className="col-md-6">
                    <Select2
                      value={form.alumno}
                      onChange={(id) => {
                        setForm((prev) => ({ ...prev, alumno: id }));
                        if (errors.alumno) setErrors((prev) => ({ ...prev, alumno: null }));
                      }}
                      fetchOptions={fetchAlumnos}
                      placeholder="Buscar alumno por nombre..."
                      label="Asignar a alumno"
                      required
                      errorMessage={errors.alumno}
                    />
                  </div>
                )}

                {/* Fecha de entrega: fecha + hora */}
                <div className={!isEditing ? "col-md-6" : "col-12"}>
                  <label className="form-label fw-medium text-secondary-light mb-8">
                    Fecha límite de entrega <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    {/* Selector de fecha */}
                    <div className="position-relative flex-grow-1">
                      <input
                        ref={datepickerRef}
                        type="text"
                        className={`form-control radius-10 ps-40 ${errors.fecha_entrega ? "is-invalid" : ""}`}
                        placeholder="dd/mm/aaaa"
                        readOnly
                      />
                      <Icon
                        icon="mdi:calendar"
                        className="position-absolute"
                        style={{
                          left: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#94a3b8",
                          fontSize: "18px",
                        }}
                      />
                    </div>
                    {/* Selector de hora */}
                    <div className="position-relative" style={{ minWidth: "120px" }}>
                      <input
                        ref={timepickerRef}
                        type="text"
                        className={`form-control radius-10 ps-42 ${errors.fecha_entrega ? "is-invalid" : ""}`}
                        placeholder="--:--"
                        readOnly
                      />
                      <Icon
                        icon="mdi:clock-outline"
                        className="position-absolute"
                        style={{
                          left: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#94a3b8",
                          fontSize: "17px",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                  {errors.fecha_entrega && (
                    <div className="invalid-feedback d-block">{errors.fecha_entrega}</div>
                  )}
                </div>
              </div>
              {/* Título */}
              <div className="mb-20">
                <label className="form-label fw-medium text-secondary-light mb-8">
                  Título <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="titulo"
                  className={`form-control radius-10 ${errors.titulo ? "is-invalid" : ""}`}
                  placeholder="Ej: Ensayo de literatura"
                  value={form.titulo}
                  onChange={handleChange}
                  maxLength={255}
                  autoFocus
                />
                {errors.titulo && (
                  <div className="invalid-feedback d-block">{errors.titulo}</div>
                )}
              </div>

              {/* Descripción */}
              <div className="mb-20">
                <label className="form-label fw-medium text-secondary-light mb-8">
                  Descripción
                </label>
                <div className="border radius-10 overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
                  <ReactQuill
                    theme="snow"
                    value={form.descripcion}
                    onChange={(val) => {
                      setForm((prev) => ({ ...prev, descripcion: val }));
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Instrucciones, detalles o notas de la actividad..."
                    style={{ minHeight: "120px" }}
                  />
                </div>
              </div>

             

            </div>

            {/* Footer */}
            <div className="modal-footer py-16 px-24 border-0 d-flex justify-content-end gap-8">
              <button
                type="button"
                className="btn btn-outline-secondary radius-10 px-16"
                onClick={onHide}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-8 radius-10 px-20"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:content-save-outline" style={{ fontSize: "18px" }} />
                    <span>{isEditing ? "Guardar cambios" : "Crear actividad"}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ActividadFormModal;
